// Pure model of a user's product experiences (`user_product_experiences`):
// release entries seen, how tours ended, which campaigns reached them. Kept
// free of React and Apollo so `experienceState.node-test.ts` covers the rules.

export type ExperienceKind = 'release' | 'tour' | 'campaign';
export type TourOutcome = 'completed' | 'dismissed';
export type DismissReason = 'skipped' | 'escape' | 'navigated_away';

export type ExperienceRow = {
  kind: string;
  key: string;
  first_seen_at: string;
  outcome?: string | null;
  outcome_at?: string | null;
  last_step?: number | null;
  step_count?: number | null;
  dismiss_reason?: string | null;
  dismiss_count?: number | null;
  holdout?: boolean | null;
};

export type OutcomeRecord = {
  outcome: TourOutcome | null; // null: shown, never finished
  at: string; // outcome time, else first seen
  dismissCount: number;
};

export type ExperienceState = {
  // release id -> when it was first seen
  seenReleases: Readonly<Record<string, string>>;
  tours: Readonly<Record<string, OutcomeRecord>>;
  campaigns: Readonly<
    Record<string, OutcomeRecord & { holdout: boolean; firstSeenAt: string }>
  >;
};

export const EMPTY_EXPERIENCES: ExperienceState = {
  seenReleases: {},
  tours: {},
  campaigns: {},
};

const isTourOutcome = (value: unknown): value is TourOutcome =>
  value === 'completed' || value === 'dismissed';

export const rowKey = (kind: string, key: string) => `${kind}:${key}`;

const toOutcomeRecord = (row: ExperienceRow): OutcomeRecord => ({
  outcome: isTourOutcome(row.outcome) ? row.outcome : null,
  at: row.outcome_at ?? row.first_seen_at,
  dismissCount: row.dismiss_count ?? 0,
});

export const toExperienceState = (
  rows: Iterable<ExperienceRow>,
): ExperienceState => {
  const seenReleases: Record<string, string> = {};
  const tours: ExperienceState['tours'] = {};
  const campaigns: Record<string, ExperienceState['campaigns'][string]> = {};
  for (const row of rows) {
    if (row.kind === 'release') {
      seenReleases[row.key] = row.first_seen_at;
    } else if (row.kind === 'tour') {
      (tours as Record<string, OutcomeRecord>)[row.key] = toOutcomeRecord(row);
    } else if (row.kind === 'campaign') {
      campaigns[row.key] = {
        ...toOutcomeRecord(row),
        holdout: row.holdout === true,
        firstSeenAt: row.first_seen_at,
      };
    }
  }
  return { seenReleases, tours, campaigns };
};

// A replayed tour that gets skipped must not erase that the user once
// completed it: the stored outcome is the best one reached.
export const shouldStoreTourOutcome = (
  previous: TourOutcome | null | undefined,
  next: TourOutcome,
): boolean => !(previous === 'completed' && next === 'dismissed');
